<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import GradientButton from "@/components/buttons/GradientButton.vue";
import { fetchQiMenChar } from "@/api/calculators";
import { QiMenChartResponse } from "@/types/calculators";
import DatePicker from "@/components/inputs/DatePicker.vue";
import { formatLocalDate } from "@/utils/date";
import QiMenResults from "@/views/Calculators/QiMen/components/QiMenResults.vue";
import PageTitleWithBackButton from "@/components/PageTitleWithBackButton.vue";
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";
import GooglePlaceAutocompleteInput from "@/components/inputs/GooglePlaceAutocompleteInput.vue";
import { PlaceSelectedPayload } from "@/types/google-places";
import AccordionFaq from "@/components/accordion/AccordionFAQ.vue";

const date = ref<Date | null>(null);
const time = ref<string | null>(null);
const system = ref<string | null>(null);
const matrixType = ref<string | null>(null);
const latitude = ref<number | null>(null);
const longitude = ref<number | null>(null);
const timeZone = ref<string | null>(null);
const qimenChart = ref<QiMenChartResponse | null>(null);
const isFieldsCorrect = computed(
  () =>
    date.value &&
    time.value &&
    system.value &&
    matrixType.value &&
    latitude.value &&
    longitude.value &&
    timeZone.value
);

const matrixTypeLabel = ref<string | null>(null);
const systemLabel = ref<string | null>(null);

const waitApiLoad = ref<boolean>(false);

const { t } = useI18n();
const { normalizeGoogleTimeZone } = useGoogleAutocomplete("ru");

const systemDropdownOptions = [
  { label: "calculators.qi-men.system-dropdown.chai-bu", value: "chai_bu" },
  { label: "calculators.qi-men.system-dropdown.chi-ren", value: "chi_run" },
];
const matrixTypeDropdownOptions = [
  { label: "calculators.qi-men.matrix-type.hourly", value: "hourly" },
  { label: "calculators.qi-men.matrix-type.daily", value: "daily" },
  { label: "calculators.qi-men.matrix-type.monthly", value: "monthly" },
  { label: "calculators.qi-men.matrix-type.yearly", value: "yearly" },
];

function handlePlaceSelected(payload: PlaceSelectedPayload) {
  latitude.value = payload.location.lat();
  longitude.value = payload.location.lng();
  timeZone.value = normalizeGoogleTimeZone(payload.timeZone);
}

async function calculateQiMen() {
  if (waitApiLoad.value) {
    return;
  }
  if (!isFieldsCorrect.value) {
    return;
  }
  systemLabel.value =
    systemDropdownOptions.find((s) => s.value === system.value)?.label ?? "";
  matrixTypeLabel.value =
    matrixTypeDropdownOptions.find((m) => m.value === matrixType.value)
      ?.label ?? "";
  waitApiLoad.value = true;
  try {
    qimenChart.value = await fetchQiMenChar(
      formatLocalDate(date.value as Date),
      time.value as string,
      system.value as string,
      matrixType.value as string,
      latitude.value as number,
      longitude.value as number,
      timeZone.value as string
    );
  } finally {
    waitApiLoad.value = false;
  }
}
</script>

<template>
  <div
    class="h-full w-full flex justify-center overflow-y-auto no-scrollbar xl:pt-[65px]"
  >
    <!-- CONTENT CONTAINER -->
    <div class="mt-6 mx-4 flex flex-col w-full md:w-[812px]">
      <!-- MOBILE BACK BTN WITH NAME -->
      <PageTitleWithBackButton
        :page-title="t('calculators.qi-men.title')"
        :title-align="'left'"
      />
      <!-- QI MEN RESULTS -->
      <QiMenResults
        v-if="qimenChart"
        :qimen-chart="qimenChart"
        :date="formatLocalDate(date)"
        :time="time"
        :system="systemLabel"
        :matrix-type="matrixTypeLabel"
      />
      <!-- QI MEN FORM -->
      <div
        class="bg-white rounded-[20px] border border-ternary h-[584px] md:h-auto pt-6"
      >
        <h1
          class="px-10 pb-10 font-medium text-[40px] leading-100p text-brown text-left hidden md:block"
          v-if="!qimenChart"
        >
          {{ t("calculators.qi-men.title") }}
        </h1>
        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10 px-4 md:px-10 md:pb-10"
        >
          <div class="flex flex-col items-start">
            <label for="date" class="label-styles">
              {{ t("calculators.qi-men.labels.date") }}
            </label>
            <DatePicker
              id="date"
              @dateSelected="(newDate: Date) => (date = newDate)"
            />
          </div>

          <div class="flex flex-col items-start">
            <label for="time" class="label-styles">
              {{ t("calculators.qi-men.labels.time") }}
            </label>
            <input
              id="time"
              v-model="time"
              type="time"
              class="border border-divider pl-4 pr-[6px] w-full h-[52px] bg-secondary rounded-[14px] sf-pro font-normal text-base text-muted"
            />
          </div>

          <div class="flex flex-col items-start">
            <label for="system" class="label-styles">
              {{ t("calculators.qi-men.labels.system") }}
            </label>
            <select
              id="system"
              v-model="system"
              class="border border-divider pl-4 pr-4 w-full h-[52px] bg-secondary rounded-[14px] sf-pro font-normal text-base text-muted cursor-pointer"
            >
              <option
                v-for="item in systemDropdownOptions"
                :key="item.value"
                :value="item.value"
              >
                {{ t(item.label) }}
              </option>
            </select>
          </div>

          <div class="flex flex-col items-start">
            <label for="type" class="label-styles">
              {{ t("calculators.qi-men.labels.matrix-type") }}
            </label>
            <select
              id="type"
              v-model="matrixType"
              class="border border-divider pl-4 pr-4 w-full h-[52px] bg-secondary rounded-[14px] sf-pro font-normal text-base text-muted cursor-pointer"
            >
              <option
                v-for="item in matrixTypeDropdownOptions"
                :key="item.value"
                :value="item.value"
              >
                {{ t(item.label) }}
              </option>
            </select>
          </div>

          <div class="flex flex-col items-start">
            <label for="city" class="label-styles">
              {{ t("labels.city_of_birth") }}
            </label>
            <GooglePlaceAutocompleteInput
              @placeSelected="handlePlaceSelected"
            />
          </div>
        </div>
        <div
          class="xl:border-t border-ternary flex items-center justify-center py-6"
        >
          <GradientButton
            class="w-[191px] h-[56px]"
            bg-color="bg-brown-gradient"
            hover-bg-color="bg-orange-gradient"
            text="Рассчитать"
            @click="calculateQiMen"
            :is-loading="waitApiLoad"
            :is-disabled="!isFieldsCorrect"
          />
        </div>
      </div>
      <!-- FAQ -->
      <div class="py-6">
        <AccordionFaq
          faqTemplateName="faq.qi-men.content"
          title="FAQ"
          :collapseButtonText="t('faq.qi-men.collapse-button')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.label-styles {
  @apply mb-2 font-semibold text-base leading-[20px] text-brown;
}
</style>
